/**
 * Asking for money, and following it to the bank.
 *
 * Raise → the department's authority approves → the Finance Head approves →
 * paid in net banking and recorded here → settled against the bank line. Only
 * that last step writes the ledger, and it writes it once: the bank line is
 * claimed by the settlement and cannot be posted again from bank statements.
 *
 * Approving happens in the approval queue, not here. This screen shows where a
 * request is on its route and who has it.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import Attachments from '../components/Attachments'
import { Card, ErrorNote, Field, Loading, Note, Pill } from '../components/ui'
import SearchSelect from '../components/SearchSelect'

interface Option {
  id: string
  code: string | null
  name: string
  note: string | null
}

interface FormOptions {
  departments: Option[]
  locations: Option[]
  cost_centers: Option[]
  categories: Option[]
  vendors: Option[]
  bank_accounts: Option[]
  //: Each approved vendor's live accounts, primary first.
  vendor_accounts?: { id: string; party_id: string; label: string; is_primary: boolean }[]
  my_department_id: string | null
  my_location_id: string | null
}

interface PaymentRequest {
  id: string
  reference: string
  kind:
    | 'ad_hoc'
    | 'purchase'
    | 'advance'
    | 'retrospective'
    | 'supplementary'
    | 'vendor_bill'
    | 'commitment'
    | 'financing'
  status: string
  title: string
  description: string | null
  justification: string | null
  amount_paise: number
  paid_paise: number
  requested_by: string
  requested_by_name: string | null
  department_id: string
  department_name: string | null
  location_id: string
  location_name: string | null
  cost_center_id: string | null
  category_id: string | null
  category_name: string | null
  party_id: string | null
  party_name: string | null
  //: The vendor account it pays into, masked.
  party_bank_account_label?: string | null
  gst_expected: boolean
  tds_expected: boolean
  required_by: string | null
  priority: string
  payment_method: string
  approval_source: 'own_route' | 'inherited'
  requisition_id: string | null
  is_cross_department: boolean
  cancel_reason: string | null
  version: number
  created_at: string
  approval_state: string
  outstanding_paise: number
  short_closed_paise: number
  close_reason: string | null
  parent_request_id: string | null
  parent_reference: string | null
  bank_transaction_id: string | null
  vendor_invoice_id: string | null
  vendor_invoice_reference: string | null
  to_pay_paise: number
}

interface SettlementRow {
  match_group_id: string | null
  bank_transaction_id: string
  txn_date: string | null
  narration: string | null
  amount_paise: number
  journal_entry_id: string
  confirmed_by_name: string | null
  confirmed_at: string
  treatment: string
  charge_paise: number
  excess_paise: number
  short_closed_paise: number
  reason: string | null
  reversed_at: string | null
  reversal_reason: string | null
}

interface Detail extends PaymentRequest {
  steps: {
    step_no: number
    label: string
    required: number
    approvals: number
    satisfied: boolean
    current: boolean
    assigned_name: string | null
  }[]
  decisions: {
    step_no: number
    decision: string
    decided_by: string
    at: string
    reason: string | null
  }[]
  payments: {
    paid_on: string
    amount_paise: number
    utr: string | null
    from_bank_account_name: string | null
    recorded_by_name: string | null
    note: string | null
  }[]
  settlement: SettlementRow | null
  settlements: SettlementRow[]
  supplementary_requests: string[]
}

interface BankLine {
  id: string
  bank_account_name: string | null
  txn_date: string
  amount_paise: number
  narration: string
  reference: string | null
  counterparty: string | null
}

const KIND_LABELS: Record<string, string> = {
  ad_hoc: 'One-off payment',
  advance: 'Advance to a vendor',
  purchase: 'Direct purchase',
  retrospective: 'Approved after payment',
  supplementary: 'Excess over an approval',
  vendor_bill: 'Vendor bill',
}

const STATUS_FILTERS = [
  ['', 'All'],
  ['draft', 'Draft'],
  ['pending_approval', 'Waiting for approval'],
  ['returned', 'Returned'],
  ['approved', 'Approved — to pay'],
  ['payment_recorded', 'Paid — to settle'],
  ['partially_paid', 'Part paid'],
  ['settled', 'Settled'],
  ['rejected', 'Rejected'],
  ['cancelled', 'Cancelled'],
] as const

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  return clean ? Math.round(Number(clean) * 100) : 0
}

const today = () => new Date().toISOString().slice(0, 10)

export default function PaymentRequests() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  // The dashboard's tiles link here with the state they counted, so the list
  // opens holding exactly those rows. Without it a tile counting four
  // payments opened a list of everything, and the four were the reader's to
  // find.
  const [params, setParams] = useSearchParams()
  const wanted = params.get('status') ?? ''
  const filter = STATUS_FILTERS.some(([value]) => value === wanted) ? wanted : ''
  const setFilter = (value: string) => {
    setParams(value ? { status: value } : {}, { replace: true })
  }
  const [open, setOpen] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const rows = useQuery({
    queryKey: ['payment-requests', filter],
    queryFn: () =>
      api.get<PaymentRequest[]>(
        `/spend/payment-requests${filter ? `?status_filter=${filter}` : ''}`,
      ),
  })
  const options = useQuery({
    queryKey: ['spend-form-options'],
    queryFn: () => api.get<FormOptions>('/spend/form-options'),
  })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['payment-requests'] })
    void queryClient.invalidateQueries({ queryKey: ['payment-request'] })
  }

  if (rows.isLoading) return <Loading what="Loading payment requests" />

  return (
    <>
      <Card
        title="Payment requests"
        hint={`${rows.data?.length ?? 0}`}
        actions={
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ width: 'auto' }}
          >
            {STATUS_FILTERS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        }
        tight
      >
        <ErrorNote error={rows.error} />
        {(rows.data ?? []).length === 0 ? (
          <div className="empty">No payment requests{filter ? ' in this state' : ' yet'}.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>For</th>
                <th>Department</th>
                <th>Raised by</th>
                <th className="num">Amount</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(rows.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td className="mono">{r.reference}</td>
                  <td>
                    {r.title}
                    <div className="faint" style={{ fontSize: 12 }}>
                      {KIND_LABELS[r.kind]}
                      {r.party_name ? ` · ${r.party_name}` : ''}
                      {r.priority === 'urgent' ? ' · urgent' : ''}
                    </div>
                  </td>
                  <td className="muted">
                    {r.department_name}
                    {r.is_cross_department && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        raised from another department
                      </div>
                    )}
                  </td>
                  <td className="muted">{r.requested_by_name}</td>
                  <td className="num">{formatInr(r.amount_paise)}</td>
                  <td>
                    <Pill value={r.status} />
                    {r.status === 'pending_approval' && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {r.approval_state}
                      </div>
                    )}
                  </td>
                  <td className="num">
                    <Link className="btn small" to={`/spending/payment-requests/${r.id}/trace`}>
                      Trace
                    </Link>{' '}
                    <button className="small" onClick={() => setOpen(open === r.id ? null : r.id)}>
                      {open === r.id ? 'Hide' : 'Open'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open && (
        <RequestDetail
          key={open}
          id={open}
          options={options.data}
          onClose={() => setOpen(null)}
          onDone={refresh}
        />
      )}

      {can('spend.payment_request.create') &&
        (creating ? (
          <CreateForm
            options={options.data}
            onCancel={() => setCreating(false)}
            onCreated={(id) => {
              setCreating(false)
              setOpen(id)
              refresh()
            }}
          />
        ) : (
          <button className="primary" onClick={() => setCreating(true)}>
            Raise a payment request
          </button>
        ))}

      <Card title="How a payment request moves">
        <p className="muted" style={{ marginBottom: 0 }}>
          The department it is for approves first, then the Finance Head — both from the{' '}
          <Link to="/books/approvals">approval queue</Link>, and never the person who raised it.
          Once approved it is paid in net banking and recorded here with its UTR, and a Book Keeper
          settles it against the bank statement line, which posts it to the books once. Purchases
          with quotations are raised from their approved purchase request on the{' '}
          <Link to="/buying/purchasing">Purchasing</Link> screen and carry that approval.
        </p>
      </Card>
    </>
  )
}

function CreateForm({
  options,
  onCancel,
  onCreated,
}: {
  options: FormOptions | undefined
  onCancel: () => void
  onCreated: (id: string) => void
}) {
  const [form, setForm] = useState({
    kind: 'ad_hoc',
    title: '',
    amount: '',
    department_id: options?.my_department_id ?? '',
    location_id: options?.my_location_id ?? '',
    cost_center_id: '',
    category_id: '',
    party_id: '',
    //: '' is the vendor's primary account.
    party_bank_account_id: '',
    required_by: '',
    priority: 'normal',
    payment_method: 'neft',
    gst_expected: false,
    tds_expected: false,
    description: '',
    justification: '',
  })
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch })
  const vendorAccounts = (options?.vendor_accounts ?? []).filter(
    (a) => a.party_id === form.party_id,
  )

  const create = useMutation({
    mutationFn: () =>
      api.post<Detail>('/spend/payment-requests', {
        kind: form.kind,
        title: form.title,
        amount_paise: toPaise(form.amount),
        department_id: form.department_id,
        location_id: form.location_id,
        cost_center_id: form.cost_center_id || null,
        category_id: form.kind === 'advance' ? null : form.category_id || null,
        party_id: form.party_id || null,
        party_bank_account_id: (form.party_id && form.party_bank_account_id) || null,
        required_by: form.required_by || null,
        priority: form.priority,
        payment_method: form.payment_method,
        gst_expected: form.gst_expected,
        tds_expected: form.tds_expected,
        description: form.description || null,
        justification: form.justification || null,
      }),
    onSuccess: (row) => onCreated(row.id),
  })

  return (
    <Card title="Raise a payment request" hint="saved as a draft; submit it when it is ready">
      <ErrorNote error={create.error} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          create.mutate()
        }}
      >
        <div className="row">
          <Field label="Kind">
            <select value={form.kind} onChange={(e) => set({ kind: e.target.value })}>
              <option value="ad_hoc">One-off payment</option>
              <option value="advance">Advance to a vendor</option>
            </select>
          </Field>
          <Field label="What it is for">
            <input
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Oxygen cylinder refill for Thane"
              required
            />
          </Field>
          <Field label="Amount (₹)">
            <input
              className="num"
              value={form.amount}
              onChange={(e) => set({ amount: e.target.value })}
              required
            />
          </Field>
        </div>
        <div className="row">
          <Field label="Department" caption="decides who approves">
            <select
              value={form.department_id}
              onChange={(e) => set({ department_id: e.target.value })}
              required
            >
              <option value="">Choose…</option>
              {(options?.departments ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Clinic">
            <select
              value={form.location_id}
              onChange={(e) => set({ location_id: e.target.value })}
              required
            >
              <option value="">Choose…</option>
              {(options?.locations ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
          {form.kind !== 'advance' && (
            <Field label="Category" caption="where it posts">
              <SearchSelect
                aria-label="Category"
                value={form.category_id}
                onChange={(v) => set({ category_id: v })}
                required
                placeholder="Choose…"
                options={(options?.categories ?? []).map((c) => ({
                  value: c.id,
                  label: c.name,
                  hint: c.note ?? undefined,
                }))}
              />
            </Field>
          )}
          {(options?.cost_centers ?? []).length > 0 && (
            <Field label="Cost centre" caption="optional">
              <select
                value={form.cost_center_id}
                onChange={(e) => set({ cost_center_id: e.target.value })}
              >
                <option value="">None</option>
                {(options?.cost_centers ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
        <div className="row">
          <Field
            label="Pay to"
            caption={form.kind === 'advance' ? 'required' : 'approved vendor, optional'}
          >
            <select
              value={form.party_id}
              onChange={(e) => set({ party_id: e.target.value, party_bank_account_id: '' })}
              required={form.kind === 'advance'}
            >
              <option value="">{form.kind === 'advance' ? 'Choose…' : 'No vendor'}</option>
              {(options?.vendors ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
          {form.party_id && vendorAccounts.length > 1 && (
            <Field label="Pay into" caption="one of the vendor's approved accounts">
              <select
                value={form.party_bank_account_id}
                onChange={(e) => set({ party_bank_account_id: e.target.value })}
              >
                <option value="">
                  Primary — {vendorAccounts.find((a) => a.is_primary)?.label ?? 'the default'}
                </option>
                {vendorAccounts
                  .filter((a) => !a.is_primary)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                    </option>
                  ))}
              </select>
            </Field>
          )}
          {form.party_id && vendorAccounts.length === 1 && (
            <Field label="Pay into" caption="the vendor's approved account">
              <input value={vendorAccounts[0].label} readOnly />
            </Field>
          )}
          <Field label="Needed by">
            <input
              type="date"
              value={form.required_by}
              onChange={(e) => set({ required_by: e.target.value })}
            />
          </Field>
          <Field label="Priority">
            <select value={form.priority} onChange={(e) => set({ priority: e.target.value })}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="urgent">Urgent</option>
            </select>
          </Field>
          <Field label="Paid by">
            <select
              value={form.payment_method}
              onChange={(e) => set({ payment_method: e.target.value })}
            >
              {['neft', 'rtgs', 'imps', 'upi', 'cheque', 'auto_debit'].map((m) => (
                <option key={m} value={m}>
                  {m.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="row">
          <Field label="GST on the invoice?">
            <select
              value={form.gst_expected ? 'yes' : 'no'}
              onChange={(e) => set({ gst_expected: e.target.value === 'yes' })}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Field>
          <Field label="TDS to deduct?">
            <select
              value={form.tds_expected ? 'yes' : 'no'}
              onChange={(e) => set({ tds_expected: e.target.value === 'yes' })}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Field>
        </div>
        {(form.gst_expected || form.tds_expected) && (
          <Note kind="warn">
            A payment carrying GST or TDS is settled through the vendor's bill, so the tax is
            accounted for. It can be raised and approved here, but not settled from this screen yet.
          </Note>
        )}
        <Field label="Details" caption="optional">
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </Field>
        <Field label="Why it is needed">
          <textarea
            rows={2}
            value={form.justification}
            onChange={(e) => set({ justification: e.target.value })}
          />
        </Field>
        <div className="row">
          <div className="shrink">
            <button className="primary" type="submit" disabled={create.isPending}>
              Save draft
            </button>{' '}
            <button type="button" className="small" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      </form>
    </Card>
  )
}

interface BudgetImpact {
  fy_code: string
  budget: {
    reference: string
    label: string
    budget_paise: number
    remaining_paise: number
    used_bp: number
  } | null
  remaining_after_paise?: number
  over: boolean
  detail: string
}

/**
 * Where the budget stands, for whoever has to decide this.
 *
 * Shown rather than enforced. An approver who can see "this takes Operations
 * to 112% of its year" decides better than one who is refused at the door and
 * watches the same request come back under a different department.
 */
function BudgetCard({ request }: { request: Detail }) {
  const { can } = useAuth()
  const { data } = useQuery({
    queryKey: ['budget-impact', request.id],
    queryFn: () =>
      api.get<BudgetImpact>(
        `/budgets/impact?amount_paise=${request.amount_paise}` +
          `&department_id=${request.department_id}` +
          `&location_id=${request.location_id}` +
          (request.category_id ? `&category_id=${request.category_id}` : ''),
      ),
    enabled: can('budget.budget.view_own'),
  })
  if (!data?.budget) return null
  return (
    <Note kind={data.over ? 'warn' : 'info'}>
      <b>{data.budget.label}</b> — {formatInr(data.budget.remaining_paise)} left of{' '}
      {formatInr(data.budget.budget_paise)} for {data.fy_code}.{' '}
      {data.over
        ? 'This request would take it over. That is not a refusal: it is what the approver is being asked to weigh.'
        : `${formatInr(data.remaining_after_paise ?? 0)} would be left after it.`}
    </Note>
  )
}

function RequestDetail({
  id,
  options,
  onClose,
  onDone,
}: {
  id: string
  options: FormOptions | undefined
  onClose: () => void
  onDone: () => void
}) {
  const { can, me } = useAuth()
  const queryClient = useQueryClient()
  const detail = useQuery({
    queryKey: ['payment-request', id],
    queryFn: () => api.get<Detail>(`/spend/payment-requests/${id}`),
  })
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['payment-request', id] })
    void queryClient.invalidateQueries({ queryKey: ['bank-lines', id] })
    onDone()
  }

  const submit = useMutation({
    mutationFn: () => api.post(`/spend/payment-requests/${id}/submit`),
    onSuccess: done,
  })
  const cancel = useMutation({
    mutationFn: ({ reason, asFinance }: { reason: string; asFinance: boolean }) =>
      api.post(`/spend/payment-requests/${id}/${asFinance ? 'void' : 'cancel'}`, { reason }),
    onSuccess: done,
  })

  if (detail.isLoading) return <Loading what="Loading the request" />
  if (detail.error || !detail.data) return <ErrorNote error={detail.error} />
  const r = detail.data
  const mine = me?.user_id === r.requested_by
  const editable = r.status === 'draft' || r.status === 'returned'
  const cancellable = !['payment_recorded', 'partially_paid', 'settled', 'cancelled'].includes(
    r.status,
  )

  return (
    <Card
      title={`${r.reference} — ${r.title}`}
      hint={KIND_LABELS[r.kind]}
      actions={
        <button className="small" onClick={onClose}>
          Close
        </button>
      }
    >
      <ErrorNote error={submit.error ?? cancel.error} />

      <div className="row" style={{ marginBottom: 8 }}>
        <div>
          <div className="caption">Amount</div>
          <b>{formatInr(r.amount_paise)}</b>
          {r.paid_paise > 0 && <div className="faint">settled {formatInr(r.paid_paise)}</div>}
          {r.paid_paise > 0 && r.outstanding_paise > 0 && (
            <div className="faint">still owed {formatInr(r.outstanding_paise)}</div>
          )}
          {r.short_closed_paise > 0 && (
            <div className="faint">short-closed {formatInr(r.short_closed_paise)}</div>
          )}
        </div>
        <div>
          <div className="caption">Status</div>
          <Pill value={r.status} />
        </div>
        <div>
          <div className="caption">Department · clinic</div>
          {r.department_name} · {r.location_name}
        </div>
        <div>
          <div className="caption">Category · pay to</div>
          {r.category_name ?? '—'} · {r.party_name ?? '—'}
          {r.party_bank_account_label && (
            <div className="faint">into {r.party_bank_account_label}</div>
          )}
        </div>
        <div>
          <div className="caption">Raised by</div>
          {r.requested_by_name}
          {r.required_by && <div className="faint">needed by {r.required_by}</div>}
        </div>
      </div>

      {r.description && <p className="muted">{r.description}</p>}
      {r.justification && (
        <p className="muted">
          <b>Why:</b> {r.justification}
        </p>
      )}
      {r.cancel_reason && <Note kind="warn">Cancelled: {r.cancel_reason}</Note>}
      {r.is_cross_department && (
        <Note kind="info">
          Raised by someone outside {r.department_name}, so {r.department_name}'s approval authority
          decides it.
        </Note>
      )}

      <BudgetCard request={r} />

      <h3 style={{ marginBottom: 4 }}>Approval</h3>
      {r.approval_source === 'inherited' ? (
        <Note kind="ok">
          {r.kind === 'vendor_bill'
            ? 'Approved with its bill, for that vendor and no more than the bill owes.'
            : 'Approved with its purchase request, for the chosen vendor at the chosen price.'}{' '}
          It does not go through the route a second time.
        </Note>
      ) : r.steps.length === 0 ? (
        <p className="faint">Not submitted yet.</p>
      ) : (
        <table style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Step</th>
              <th>Who</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {r.steps.map((s) => {
              const decided = r.decisions.filter((d) => d.step_no === s.step_no)
              return (
                <tr key={s.step_no}>
                  <td>
                    {s.step_no}. {s.label}
                  </td>
                  <td className="muted">{s.assigned_name ?? 'anyone who may approve'}</td>
                  <td>
                    {s.satisfied ? (
                      <Pill value="approved" />
                    ) : s.current ? (
                      <Pill value="waiting" />
                    ) : (
                      <span className="faint">not yet</span>
                    )}
                    {decided.map((d) => (
                      <div key={d.at} className="faint" style={{ fontSize: 12 }}>
                        {d.decision} by {d.decided_by} on {d.at.slice(0, 10)}
                        {d.reason ? ` — ${d.reason}` : ''}
                      </div>
                    ))}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <div className="caption">Supporting documents</div>
      <Attachments
        subjectType="payment_request"
        subjectId={r.id}
        kind="invoice"
        canAttach={mine || can('spend.payment_request.reconcile')}
      />

      <div className="row" style={{ marginTop: 12 }}>
        <div className="shrink">
          {editable && mine && can('spend.payment_request.submit') && (
            <button className="primary" disabled={submit.isPending} onClick={() => submit.mutate()}>
              {r.status === 'returned' ? 'Resubmit for approval' : 'Submit for approval'}
            </button>
          )}{' '}
          {cancellable && mine && can('spend.payment_request.edit') && (
            <button
              className="small"
              onClick={() => {
                const reason = window.prompt('Why is this request being cancelled?')
                if (reason && reason.trim()) cancel.mutate({ reason, asFinance: false })
              }}
            >
              Cancel request
            </button>
          )}{' '}
          {cancellable && !mine && can('spend.payment_request.void') && (
            <button
              className="small danger"
              onClick={() => {
                const reason = window.prompt('Why should this request not be paid?')
                if (reason && reason.trim()) cancel.mutate({ reason, asFinance: true })
              }}
            >
              Void
            </button>
          )}
        </div>
      </div>

      {r.payments.length > 0 && (
        <>
          <h3 style={{ marginBottom: 4 }}>Payment</h3>
          {r.payments.map((p) => (
            <p key={p.paid_on + (p.utr ?? '')} className="muted" style={{ margin: '0 0 4px' }}>
              {formatInr(p.amount_paise)} paid on {p.paid_on}
              {p.utr ? `, UTR ${p.utr}` : ''}
              {p.from_bank_account_name ? `, from ${p.from_bank_account_name}` : ''}
              {p.recorded_by_name ? ` — recorded by ${p.recorded_by_name}` : ''}
            </p>
          ))}
        </>
      )}

      {r.vendor_invoice_reference ? (
        <Note kind="info">
          Pays bill {r.vendor_invoice_reference}: {formatInr(r.to_pay_paise)} to the vendor, after
          the TDS the bill withheld. Settling it takes that off the bill.
        </Note>
      ) : (
        (r.gst_expected || r.tds_expected) &&
        ['approved', 'payment_recorded'].includes(r.status) &&
        can('spend.payment_request.reconcile') && <LinkBill request={r} onDone={done} />
      )}

      {(r.parent_reference || r.bank_transaction_id || r.supplementary_requests.length > 0) && (
        <Note kind="info">
          {r.parent_reference && <>Approves the excess the bank paid over {r.parent_reference}. </>}
          {r.bank_transaction_id && (
            <>Raised after the money had left, to approve that debit; it settles only that line. </>
          )}
          {r.supplementary_requests.length > 0 && (
            <>Excess over this request: {r.supplementary_requests.join(', ')}.</>
          )}
        </Note>
      )}

      {(r.status === 'approved' || r.status === 'partially_paid') &&
        !r.bank_transaction_id &&
        can('spend.payment_request.release') && (
          <RecordPayment request={r} options={options} onDone={done} />
        )}

      {r.settlements.length > 0 && (
        <>
          <h3 style={{ marginBottom: 4 }}>Settlement</h3>
          {r.settlements.map((s) => (
            <p
              key={s.confirmed_at}
              className="muted"
              style={{
                margin: '0 0 4px',
                textDecoration: s.reversed_at ? 'line-through' : undefined,
              }}
            >
              {formatInr(s.amount_paise)} against the bank line of {s.txn_date} ({s.narration}),{' '}
              {s.treatment.replace(/_/g, ' ')}
              {s.charge_paise > 0 ? `, bank charge ${formatInr(s.charge_paise)}` : ''}
              {s.excess_paise > 0 ? `, ${formatInr(s.excess_paise)} to suspense` : ''}
              {s.short_closed_paise > 0
                ? `, ${formatInr(s.short_closed_paise)} short-closed`
                : ''}{' '}
              — posted by {s.confirmed_by_name} on {s.confirmed_at.slice(0, 10)}
              {s.reason ? `: ${s.reason}` : ''}
              {s.reversed_at && (
                <span style={{ textDecoration: 'none' }}>
                  {' '}
                  (reversed {s.reversed_at.slice(0, 10)}: {s.reversal_reason})
                </span>
              )}
            </p>
          ))}
          {r.settlement && can('compliance.exception.approve') && (
            <Note kind="info">
              To undo a settlement, reverse it on the{' '}
              <Link to="/control/reconciliation">reconciliation screen</Link>.
            </Note>
          )}
        </>
      )}

      {['approved', 'payment_recorded', 'partially_paid'].includes(r.status) &&
        can('spend.payment_request.reconcile') && <Settle request={r} onDone={done} />}
    </Card>
  )
}

function LinkBill({ request, onDone }: { request: Detail; onDone: () => void }) {
  const bills = useQuery({
    queryKey: ['bills-to-link', request.id],
    queryFn: () =>
      api.get<
        {
          id: string
          reference: string
          supplier_invoice_no: string
          invoice_date: string
          total_paise: number
          outstanding_paise: number
        }[]
      >(`/spend/payment-requests/${request.id}/bills`),
  })
  const [chosen, setChosen] = useState('')
  const link = useMutation({
    mutationFn: () =>
      api.post(`/spend/payment-requests/${request.id}/link-bill`, { vendor_invoice_id: chosen }),
    onSuccess: onDone,
  })
  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={{ marginBottom: 4 }}>Link the vendor's bill</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        This payment carries GST or TDS, so it is settled against the vendor's posted bill, which
        accounts for the tax and says what the vendor is actually owed.
      </p>
      <ErrorNote error={bills.error ?? link.error} />
      {(bills.data ?? []).length === 0 ? (
        <p className="faint">
          No posted bill from this vendor is waiting to be paid. Enter and post it on Bills first.
        </p>
      ) : (
        <div className="row">
          <Field label="Bill">
            <select value={chosen} onChange={(e) => setChosen(e.target.value)}>
              <option value="">Choose…</option>
              {(bills.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.reference} ({b.supplier_invoice_no}, {b.invoice_date}) — owes{' '}
                  {formatInr(b.outstanding_paise)}
                </option>
              ))}
            </select>
          </Field>
          <div className="shrink">
            <button
              className="primary small"
              disabled={!chosen || link.isPending}
              onClick={() => link.mutate()}
            >
              Link bill
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function RecordPayment({
  request,
  options,
  onDone,
}: {
  request: Detail
  options: FormOptions | undefined
  onDone: () => void
}) {
  const [form, setForm] = useState({
    paid_on: today(),
    utr: '',
    from_bank_account_id: '',
    note: '',
  })
  const record = useMutation({
    mutationFn: () =>
      api.post(`/spend/payment-requests/${request.id}/payments`, {
        paid_on: form.paid_on,
        amount_paise: request.outstanding_paise,
        utr: form.utr || null,
        from_bank_account_id: form.from_bank_account_id || null,
        note: form.note || null,
      }),
    onSuccess: onDone,
  })
  return (
    <form
      style={{ marginTop: 12 }}
      onSubmit={(e) => {
        e.preventDefault()
        record.mutate()
      }}
    >
      <h3 style={{ marginBottom: 4 }}>Record the payment</h3>
      <ErrorNote error={record.error} />
      <div className="row">
        <Field label="Paid on">
          <input
            type="date"
            max={today()}
            value={form.paid_on}
            onChange={(e) => setForm({ ...form, paid_on: e.target.value })}
            required
          />
        </Field>
        <Field label="UTR / reference" caption="from the bank's confirmation">
          <input value={form.utr} onChange={(e) => setForm({ ...form, utr: e.target.value })} />
        </Field>
        <Field label="From account">
          <select
            value={form.from_bank_account_id}
            onChange={(e) => setForm({ ...form, from_bank_account_id: e.target.value })}
          >
            <option value="">Choose…</option>
            {(options?.bank_accounts ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} {b.note ? `(${b.note})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <div className="shrink">
          <button className="primary" type="submit" disabled={record.isPending}>
            Record {formatInr(request.outstanding_paise)} paid
          </button>
        </div>
      </div>
      <Note kind="info">
        Recording a payment posts nothing. It notes that the transfer was made in net banking, so
        the bank line can be found by its UTR when the statement arrives.
      </Note>
    </form>
  )
}

function Settle({ request, onDone }: { request: Detail; onDone: () => void }) {
  const lines = useQuery({
    queryKey: ['bank-lines', request.id],
    queryFn: () => api.get<BankLine[]>(`/spend/payment-requests/${request.id}/bank-lines`),
  })
  const settle = useMutation({
    mutationFn: (bank_transaction_id: string) =>
      api.post(`/spend/payment-requests/${request.id}/settle`, { bank_transaction_id }),
    onSuccess: onDone,
  })

  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={{ marginBottom: 4 }}>Settle against the bank statement</h3>
      <ErrorNote error={lines.error ?? settle.error} />
      {lines.isLoading ? (
        <Loading what="Looking for the bank line" />
      ) : (lines.data ?? []).length === 0 ? (
        <p className="faint">
          No open bank debit of {formatInr(request.outstanding_paise)} (or a little more, for a bank
          charge) near the payment date. Import the statement that shows it, then look again.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Account</th>
              <th>Narration</th>
              <th className="num">Amount</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(lines.data ?? []).map((t) => (
              <tr key={t.id}>
                <td className="muted date">{t.txn_date}</td>
                <td className="muted">{t.bank_account_name}</td>
                <td>
                  {t.narration}
                  {t.reference && (
                    <div className="faint mono" style={{ fontSize: 12 }}>
                      {t.reference}
                    </div>
                  )}
                </td>
                <td className="num">{formatInr(t.amount_paise)}</td>
                <td className="num">
                  <button
                    className="small primary"
                    disabled={settle.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Settle ${request.reference} against this line and post it to the books?`,
                        )
                      ) {
                        settle.mutate(t.id)
                      }
                    }}
                  >
                    Settle &amp; post
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
